import { withPrefix } from 'gatsby';
import parse from 'html-react-parser';
import React from 'react';

import Presenter from '~/components/HCP/Presenter/Presenter';
import { Translations } from '~/context/TranslationContext/models';

import { PresenterType } from './models';

export enum EventType {
  ONLINE = 'Online',
  ON_SITE = 'On-site',
  WORKSHOP = 'Workshop',
  NETWORKING = 'Networking',
}

export const ICON_BY_TYPE: Record<EventType, string> = {
  [EventType.ONLINE]: 'online.svg',
  [EventType.ON_SITE]: 'home.svg',
  [EventType.WORKSHOP]: 'workshop.svg',
  [EventType.NETWORKING]: 'people.svg',
};

export const EVENT_TITLE: Record<EventType, string> = {
  [EventType.ONLINE]: 'Online Event',
  [EventType.ON_SITE]: 'On-site',
  [EventType.WORKSHOP]: 'Worshop',
  [EventType.NETWORKING]: 'Offline Event',
};

export type EventTitle = typeof EVENT_TITLE[EventType];

type DataTime = {
  formattedSimpleDate: string;
  formattedDate: string;
  formattedTime: string;
  formattedTimeShort: string;
  timeZoneNameShort: string;
};

export function getEventTypeIconSrc(type: EventType): string {
  return withPrefix(`/images/${ICON_BY_TYPE[type]}`);
}

const calendarIcon = (
  <img src={withPrefix('/images/calendar.svg')} alt="calendar.svg" />
);
const clockIcon = <img src={withPrefix('/images/clock.svg')} alt="clock.svg" />;

export const getEventTitle = (t: Translations, type: EventType): string => {
  switch (type) {
    case EventType.ONLINE:
      return t?.eventPage?.onlineEvent;
    case EventType.ON_SITE:
      return t?.eventPage?.onSite;
    case EventType.WORKSHOP:
      return t?.eventPage?.worshop;
    case EventType.NETWORKING:
      return t?.eventPage?.offlineEvent;
    default:
      return t?.eventPage?.onlineEvent;
  }
};

export const mapEventDetails = (
  t: Translations,
  start: DataTime,
  end: DataTime,
  typeIcon: React.ReactNode,
  eventType: EventType
) => [
  {
    icon: calendarIcon,
    title: t?.eventPage?.date,
    description: start.formattedDate,
  },
  {
    icon: clockIcon,
    title: t?.eventPage?.time,
    description: `${start.formattedTimeShort} to ${end.formattedTimeShort}${
      start.timeZoneNameShort ? ` ${start.timeZoneNameShort}` : ''
    }`,
  },
  {
    icon: typeIcon,
    title: getEventTitle(t, eventType),
    description: t.eventPage.myEvents || 'My Events',
  },
];

export function presentersMapper(presenters: PresenterType[]) {
  return presenters.map(
    ({
      about_presenter,
      presenter_image,
      presenter_name,
      presenter_last_name,
      city,
      country,
    }) => {
      const name = `${presenter_name}  ${presenter_last_name}`;
      const presenterLocation = country && city ? `${city},  ${country}` : '';

      return {
        title: (
          <Presenter
            font="s"
            gap="md"
            size={60}
            name={name}
            avatarUrl={presenter_image.image.url}
            presenterLocation={presenterLocation}
            isPresenterLocationDisplayed
          />
        ),
        children: parse(about_presenter),
      };
    }
  );
}

export const isPastEvent = (eventTimestamp?: {
  event_start_date?: string;
  event_end_date?: string;
}): boolean => {
  if (!eventTimestamp?.event_end_date) {
    return false;
  }

  const now = Date.now();
  const eventEnd = Date.parse(eventTimestamp.event_end_date);

  return eventEnd < now;
};
